import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Video,
  Copy,
  Check,
  Trash2,
  HelpCircle,
  Star,
  Facebook,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { FERRAMENTAS } from "./templates";
import { gerarPost, gerarRoteiro, gerarRoteiroNarrado, gerarRoteiroTutorial, contarPalavras, duracaoNarracao } from "./generator";
import GerarVideoRoteiroModal from "./GerarVideoRoteiroModal";

type ContentRow = {
  id: string;
  tipo: "facebook_post" | "video_roteiro";
  ferramenta: string;
  titulo: string;
  conteudo: string;
  potencia: number;
  eficacia: number;
  usado: boolean;
  created_at: string;
};

const ContentGeneratorPanel: React.FC = () => {
  const { user } = useAuth();
  const [ferramenta, setFerramenta] = useState<string>("");
  const [items, setItems] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterTipo, setFilterTipo] = useState<string>("all");
  const [videoRoteiro, setVideoRoteiro] = useState<ContentRow | null>(null);
  const [narrado, setNarrado] = useState(true);
  const [formatoRoteiro, setFormatoRoteiro] = useState<"tutorial" | "narrado" | "curto">("tutorial");
  const [duracaoTutorial, setDuracaoTutorial] = useState<"3" | "5">("5");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("content_generated" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (!error && data) setItems(data as any);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  async function handleGerar(tipo: "facebook_post" | "video_roteiro") {
    if (!user?.id) {
      toast.error("Usuário não identificado");
      return;
    }
    const gen =
      tipo === "facebook_post"
        ? gerarPost(ferramenta || undefined)
        : formatoRoteiro === "tutorial"
        ? gerarRoteiroTutorial(ferramenta || undefined, duracaoTutorial === "3" ? 3 : 5)
        : formatoRoteiro === "narrado"
        ? gerarRoteiroNarrado(ferramenta || undefined)
        : gerarRoteiro(ferramenta || undefined);
    const { error } = await supabase.from("content_generated" as any).insert({
      user_id: user.id,
      ...gen,
    } as any);
    if (error) {
      toast.error("Erro ao salvar: " + error.message);
      return;
    }
    const extra =
      tipo === "video_roteiro" && formatoRoteiro !== "curto"
        ? ` · ${contarPalavras(gen.conteudo)} palavras · ~${duracaoNarracao(gen.conteudo)} de narração`
        : "";
    toast.success(`${tipo === "facebook_post" ? "Post" : "Roteiro"} gerado! ⭐ ${gen.potencia}/5 · ${gen.eficacia}%${extra}`);
    fetchItems();
  }

  async function handleCopy(txt: string) {
    await navigator.clipboard.writeText(txt);
    toast.success("Copiado!");
  }

  async function handleToggleUsado(row: ContentRow) {
    await supabase
      .from("content_generated" as any)
      .update({ usado: !row.usado } as any)
      .eq("id", row.id);
    fetchItems();
  }

  async function handleDelete(id: string) {
    await supabase.from("content_generated" as any).delete().eq("id", id);
    fetchItems();
  }

  const filtered = items.filter((i) => (filterTipo === "all" ? true : i.tipo === filterTipo));

  return (
    <div className="space-y-6">
      <Card className="border-2 border-gold-500/30 bg-gradient-to-br from-background to-gold-500/5">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-gold-500" />
                Gerador de Conteúdo
              </CardTitle>
              <CardDescription>
                Gere posts para Facebook e roteiros de vídeo com 1 clique. Cada peça recebe
                Potência (1-5) e Eficácia (%) estimadas.
              </CardDescription>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Como gerar mais poder
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Como aumentar Potência e Eficácia</DialogTitle>
                </DialogHeader>
                <div className="text-sm space-y-3 text-muted-foreground">
                  <p><strong>Potência (⭐ 1-5):</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Use um <strong>número/estatística</strong> ("2000 apostadores")</li>
                    <li>Comece com <strong>gancho de dor</strong> ("Cansado de errar?")</li>
                    <li>Adicione <strong>prova social</strong> (depoimentos)</li>
                    <li>Crie <strong>urgência</strong> ("hoje", "agora", "corre")</li>
                    <li>Termine com <strong>CTA claro</strong> (link, botão)</li>
                  </ul>
                  <p className="pt-2"><strong>Eficácia (%):</strong> combina potência, comprimento ideal (40-150 palavras) e uso moderado de emojis (3-10).</p>
                  <p className="pt-2 text-xs">Gerou algo fraco? Clique novamente — cada geração sorteia um template diferente.</p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Ferramenta (opcional)</label>
              <Select value={ferramenta || "random"} onValueChange={(v) => setFerramenta(v === "random" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Aleatório" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">🎲 Aleatório</SelectItem>
                  {FERRAMENTAS.map((f) => (
                    <SelectItem key={f.key} value={f.key}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => handleGerar("facebook_post")}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Facebook className="w-4 h-4 mr-2" />
              Gerar Post Facebook
            </Button>
            <div className="min-w-[260px]">
              <label className="text-sm font-medium mb-1 block">Formato do roteiro</label>
              <Select
                value={formatoRoteiro}
                onValueChange={(v) => {
                  setFormatoRoteiro(v as "tutorial" | "narrado" | "curto");
                  setNarrado(v !== "curto");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutorial">🎥 Tutorial (narrar usando a ferramenta)</SelectItem>
                  <SelectItem value="narrado">🎙️ Roteiro narrado (~5 min)</SelectItem>
                  <SelectItem value="curto">⚡ Curto (Reels / TikTok / Shorts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formatoRoteiro === "tutorial" && (
              <div className="min-w-[150px]">
                <label className="text-sm font-medium mb-1 block">Duração</label>
                <Select value={duracaoTutorial} onValueChange={(v) => setDuracaoTutorial(v as "3" | "5")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">~3 minutos</SelectItem>
                    <SelectItem value="5">~5 minutos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={() => handleGerar("video_roteiro")}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Video className="w-4 h-4 mr-2" />
              {formatoRoteiro === "tutorial"
                ? `Gerar Roteiro Tutorial (${duracaoTutorial} min)`
                : formatoRoteiro === "narrado"
                ? "Gerar Roteiro Narrado (5 min)"
                : "Gerar Roteiro de Vídeo"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Histórico ({filtered.length})</CardTitle>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="facebook_post">Posts Facebook</SelectItem>
                <SelectItem value="video_roteiro">Roteiros de vídeo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Carregando...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum conteúdo gerado ainda. Clique nos botões acima para começar!
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((row) => (
                <div
                  key={row.id}
                  className={`border rounded-lg p-4 ${row.usado ? "opacity-60 bg-muted/30" : "bg-card"}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={row.tipo === "facebook_post" ? "default" : "secondary"}>
                        {row.tipo === "facebook_post" ? "📘 Facebook" : "🎬 Vídeo"}
                      </Badge>
                      <span className="font-semibold text-sm">{row.titulo}</span>
                      <span className="text-yellow-500 text-sm flex items-center">
                        {Array.from({ length: row.potencia }).map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-current" />
                        ))}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {row.eficacia}% eficácia
                      </Badge>
                      {row.usado && <Badge variant="outline" className="text-xs">✅ Usado</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(row.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm bg-muted/40 p-3 rounded border text-foreground font-sans">
                    {row.conteudo}
                  </pre>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => handleCopy(row.conteudo)}>
                      <Copy className="w-3 h-3 mr-1" />
                      Copiar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleToggleUsado(row)}>
                      <Check className="w-3 h-3 mr-1" />
                      {row.usado ? "Marcar não usado" : "Marcar usado"}
                    </Button>
                    {row.tipo === "video_roteiro" && (
                      <Button
                        size="sm"
                        onClick={() => setVideoRoteiro(row)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                      >
                        <Video className="w-3 h-3 mr-1" />
                        🎥 Gerar Vídeo deste Roteiro
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(row.id)}>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {videoRoteiro && (
        <GerarVideoRoteiroModal
          open={!!videoRoteiro}
          onOpenChange={(v) => !v && setVideoRoteiro(null)}
          titulo={videoRoteiro.titulo}
          conteudo={videoRoteiro.conteudo}
        />
      )}
    </div>
  );
};

export default ContentGeneratorPanel;
